/**
 * AppManifestForm
 *
 * Create / Edit form for an app catalogue manifest (Appendix E schema).
 * Covers every manifest field: identity, branding, iOS + Android platform
 * blocks, versions, the entitlement predicate, categories, deep links, the web
 * URL, feature flags and metadata.
 *
 * Bumping `minAppVersion` is intercepted with a disruption-warning dialog
 * (Appendix E — "Force everyone to update").
 */

import { useMemo, useState } from 'react';
import {
  IconDeviceMobile,
  IconAlertTriangle,
  IconBrandApple,
  IconBrandAndroid,
  IconShieldCheck,
  IconTag,
  IconFlag,
  IconInfoCircle,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import {
  Stack,
  Group,
  Card,
  Title,
  Text,
  TextInput,
  Textarea,
  MultiSelect,
  Switch,
  Button,
  Badge,
  Divider,
  Alert,
  ColorInput,
  ActionIcon,
  SimpleGrid,
  Modal,
} from '@/components/ui';
import type { AppManifest, AppCategory } from '@actschurch/ccms-sdk';
import { createEmptyAppManifest } from '@actschurch/ccms-sdk';
import { EntitlementPreview } from './EntitlementPreview';

const CATEGORY_OPTIONS: { value: AppCategory; label: string }[] = [
  { value: 'core', label: 'Core' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'people', label: 'People' },
  { value: 'finance', label: 'Finance' },
  { value: 'events', label: 'Events' },
  { value: 'learning', label: 'Learning' },
  { value: 'social', label: 'Social' },
];

const AUDIENCE_OPTIONS = ['staff', 'vendor', 'public'];

const APP_ID_PATTERN = /^ccms\.[a-z][a-z0-9-]*$/;
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

/** Compare two semver strings. Returns >0 if a>b, <0 if a<b, 0 if equal. */
function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  }
  return 0;
}

interface AppManifestFormProps {
  /** Existing manifest when editing; undefined when creating. */
  initial?: AppManifest;
  /** Whether a save is in flight. */
  saving?: boolean;
  /** Called with the assembled manifest when the user saves. */
  onSubmit: (manifest: AppManifest) => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

/** Small helper for editing a string[] as a list of chips + add field. */
function StringListEditor({
  label,
  description,
  placeholder,
  values,
  onChange,
}: {
  label: string;
  description?: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) {
      onChange([...values, v]);
    }
    setDraft('');
  };

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
      <Group gap="xs">
        <TextInput
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          style={{ flex: 1 }}
        />
        <ActionIcon variant="light" onClick={add} aria-label={`Add ${label}`}>
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
      {values.length > 0 && (
        <Group gap={6} mt={4}>
          {values.map((v) => (
            <Badge
              key={v}
              variant="light"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="gray"
                  onClick={() => onChange(values.filter((x) => x !== v))}
                  aria-label={`Remove ${v}`}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {v}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}

export function AppManifestForm({ initial, saving, onSubmit, onCancel }: AppManifestFormProps) {
  const isEdit = !!initial;
  const [manifest, setManifest] = useState<AppManifest>(
    () => initial ?? createEmptyAppManifest()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bumpDialogOpen, setBumpDialogOpen] = useState(false);

  // The minAppVersion the user is editing away from (for the bump warning).
  const originalMinAppVersion = initial?.minAppVersion ?? '1.0.0';

  // Feature-flag editing draft.
  const [flagKey, setFlagKey] = useState('');

  const update = (patch: Partial<AppManifest>) => {
    setManifest((m) => ({ ...m, ...patch }));
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!APP_ID_PATTERN.test(manifest.appId)) {
      e.appId = 'Must match ^ccms.<lowercase-id> (e.g. ccms.task)';
    }
    if (!manifest.displayName.trim()) e.displayName = 'Display name is required';
    if (manifest.displayName.length > 60) e.displayName = 'Max 60 characters';
    if (!manifest.shortName.trim()) e.shortName = 'Short name is required';
    if (manifest.shortName.length > 16) e.shortName = 'Max 16 characters';
    if ((manifest.description ?? '').length > 280) e.description = 'Max 280 characters';
    if (!manifest.iconUrl.trim()) e.iconUrl = 'Icon URL is required';
    if (!SEMVER_PATTERN.test(manifest.minAppVersion)) {
      e.minAppVersion = 'Must be a semver, e.g. 1.0.0';
    }
    if (manifest.minHubVersion && !SEMVER_PATTERN.test(manifest.minHubVersion)) {
      e.minHubVersion = 'Must be a semver, e.g. 1.0.0';
    }
    if (!manifest.defaultDeepLink.startsWith('/')) {
      e.defaultDeepLink = 'Must start with /';
    }
    if (!manifest.webUrl.trim()) e.webUrl = 'Web URL is required';
    if (!manifest.platforms.ios.bundleId.trim()) e.iosBundleId = 'iOS bundle id is required';
    if (!manifest.platforms.android.applicationId.trim()) {
      e.androidApplicationId = 'Android applicationId is required';
    }
    return e;
  };

  const minAppVersionBumped = useMemo(
    () =>
      isEdit &&
      SEMVER_PATTERN.test(manifest.minAppVersion) &&
      compareSemver(manifest.minAppVersion, originalMinAppVersion) > 0,
    [isEdit, manifest.minAppVersion, originalMinAppVersion]
  );

  const finalise = () => {
    onSubmit({
      ...manifest,
      metadata: {
        ...manifest.metadata,
        updatedAt: new Date().toISOString(),
        ...(isEdit ? {} : { createdAt: new Date().toISOString() }),
      },
    });
  };

  const handleSubmit = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    // Appendix E: bumping minAppVersion forces a user-disruptive update.
    if (minAppVersionBumped) {
      setBumpDialogOpen(true);
      return;
    }
    finalise();
  };

  const ios = manifest.platforms.ios;
  const android = manifest.platforms.android;
  const featureFlags = manifest.featureFlags ?? {};

  return (
    <Stack gap="lg">
      {/* Identity */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconDeviceMobile size={18} />
          <Title order={4}>Identity</Title>
        </Group>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="App ID"
              description="Stable id, e.g. ccms.task. Cannot change after creation."
              placeholder="ccms.task"
              value={manifest.appId}
              onChange={(e) => update({ appId: e.currentTarget.value })}
              error={errors.appId}
              disabled={isEdit}
              required
            />
            <TextInput
              label="Icon URL"
              description="Must be on a CCMS-controlled CDN."
              placeholder="https://cdn.ccms.actschurch.co.za/app-icons/ccms-task.png"
              value={manifest.iconUrl}
              onChange={(e) => update({ iconUrl: e.currentTarget.value })}
              error={errors.iconUrl}
              required
            />
            <TextInput
              label="Display Name"
              description="Max 60 characters."
              placeholder="CCMS Tasks"
              value={manifest.displayName}
              onChange={(e) => update({ displayName: e.currentTarget.value })}
              error={errors.displayName}
              required
            />
            <TextInput
              label="Short Name"
              description="Launcher tile label. Max 16 characters."
              placeholder="Tasks"
              value={manifest.shortName}
              onChange={(e) => update({ shortName: e.currentTarget.value })}
              error={errors.shortName}
              required
            />
          </SimpleGrid>
          <Textarea
            label="Description"
            description="Max 280 characters."
            placeholder="Your tasks, your assignments, your day — on the go."
            value={manifest.description ?? ''}
            onChange={(e) => update({ description: e.currentTarget.value })}
            error={errors.description}
            autosize
            minRows={2}
          />
        </Stack>
      </Card>

      {/* Branding */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconTag size={18} />
          <Title order={4}>Branding & Categories</Title>
        </Group>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <ColorInput
              label="Primary Colour"
              placeholder="#5B6CFF"
              value={manifest.colors?.primary ?? '#5B6CFF'}
              onChange={(v) =>
                update({
                  colors: {
                    primary: v,
                    onPrimary: manifest.colors?.onPrimary ?? '#FFFFFF',
                  },
                })
              }
            />
            <ColorInput
              label="On-Primary Colour"
              placeholder="#FFFFFF"
              value={manifest.colors?.onPrimary ?? '#FFFFFF'}
              onChange={(v) =>
                update({
                  colors: {
                    primary: manifest.colors?.primary ?? '#5B6CFF',
                    onPrimary: v,
                  },
                })
              }
            />
          </SimpleGrid>
          <MultiSelect
            label="Categories"
            placeholder="Pick categories"
            data={CATEGORY_OPTIONS}
            value={manifest.categories as string[]}
            onChange={(v) => update({ categories: v as AppCategory[] })}
            clearable
            searchable
          />
          <MultiSelect
            label="Audience"
            placeholder="Pick audience"
            description="vendor apps may only carry Vendor-only entitlement (server-enforced)."
            data={AUDIENCE_OPTIONS}
            value={manifest.audience ? [manifest.audience] : ['staff']}
            onChange={(v) => update({ audience: v[v.length - 1] ?? 'staff' })}
          />
        </Stack>
      </Card>

      {/* Versions & links */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconInfoCircle size={18} />
          <Title order={4}>Versions & Links</Title>
        </Group>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Minimum App Version"
              description="Bumping this forces a user-disruptive update."
              placeholder="1.0.0"
              value={manifest.minAppVersion}
              onChange={(e) => update({ minAppVersion: e.currentTarget.value })}
              error={errors.minAppVersion}
              rightSection={
                minAppVersionBumped ? (
                  <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
                ) : undefined
              }
              required
            />
            <TextInput
              label="Minimum Hub Version"
              placeholder="1.0.0"
              value={manifest.minHubVersion ?? ''}
              onChange={(e) => update({ minHubVersion: e.currentTarget.value })}
              error={errors.minHubVersion}
            />
            <TextInput
              label="Default Deep Link"
              description="Must start with /."
              placeholder="/app/tasks/my-tasks"
              value={manifest.defaultDeepLink}
              onChange={(e) => update({ defaultDeepLink: e.currentTarget.value })}
              error={errors.defaultDeepLink}
              required
            />
            <TextInput
              label="Web URL"
              description="On https://ccms.actschurch.co.za."
              placeholder="https://ccms.actschurch.co.za/app/tasks"
              value={manifest.webUrl}
              onChange={(e) => update({ webUrl: e.currentTarget.value })}
              error={errors.webUrl}
              required
            />
          </SimpleGrid>
          {minAppVersionBumped && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Version bump detected">
              Raising the minimum app version from <strong>{originalMinAppVersion}</strong> to{' '}
              <strong>{manifest.minAppVersion}</strong> will prompt every outdated install to update
              on next cold-start. You will be asked to confirm before saving.
            </Alert>
          )}
          <StringListEditor
            label="Dashboard Widget IDs"
            description="Widget ids this app contributes to the dashboard aggregator."
            placeholder="task.my-open-count"
            values={manifest.dashboardWidgetIds ?? []}
            onChange={(v) => update({ dashboardWidgetIds: v })}
          />
        </Stack>
      </Card>

      {/* iOS platform */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconBrandApple size={18} />
          <Title order={4}>iOS Platform</Title>
        </Group>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Bundle ID"
              placeholder="co.za.actschurch.ccms.task"
              value={ios.bundleId}
              onChange={(e) =>
                update({ platforms: { ...manifest.platforms, ios: { ...ios, bundleId: e.currentTarget.value } } })
              }
              error={errors.iosBundleId}
              required
            />
            <TextInput
              label="Minimum OS Version"
              placeholder="16.0"
              value={ios.minOSVersion}
              onChange={(e) =>
                update({ platforms: { ...manifest.platforms, ios: { ...ios, minOSVersion: e.currentTarget.value } } })
              }
            />
            <TextInput
              label="Store URL"
              placeholder="https://apps.apple.com/za/app/..."
              value={ios.storeUrl}
              onChange={(e) =>
                update({ platforms: { ...manifest.platforms, ios: { ...ios, storeUrl: e.currentTarget.value } } })
              }
            />
            <TextInput
              label="Custom URL Scheme"
              placeholder="ccmstask"
              value={ios.customScheme}
              onChange={(e) =>
                update({ platforms: { ...manifest.platforms, ios: { ...ios, customScheme: e.currentTarget.value } } })
              }
            />
          </SimpleGrid>
          <StringListEditor
            label="Universal Link Paths"
            placeholder="/app/tasks/*"
            values={ios.universalLinkPaths}
            onChange={(v) =>
              update({ platforms: { ...manifest.platforms, ios: { ...ios, universalLinkPaths: v } } })
            }
          />
        </Stack>
      </Card>

      {/* Android platform */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconBrandAndroid size={18} />
          <Title order={4}>Android Platform</Title>
        </Group>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Application ID"
              placeholder="co.za.actschurch.ccms.task"
              value={android.applicationId}
              onChange={(e) =>
                update({
                  platforms: {
                    ...manifest.platforms,
                    android: { ...android, applicationId: e.currentTarget.value },
                  },
                })
              }
              error={errors.androidApplicationId}
              required
            />
            <TextInput
              label="Minimum API Level"
              placeholder="26"
              value={android.minOSVersion}
              onChange={(e) =>
                update({
                  platforms: {
                    ...manifest.platforms,
                    android: { ...android, minOSVersion: e.currentTarget.value },
                  },
                })
              }
            />
            <TextInput
              label="Store URL"
              placeholder="https://play.google.com/store/apps/details?id=..."
              value={android.storeUrl}
              onChange={(e) =>
                update({
                  platforms: {
                    ...manifest.platforms,
                    android: { ...android, storeUrl: e.currentTarget.value },
                  },
                })
              }
            />
            <TextInput
              label="Custom URL Scheme"
              placeholder="ccmstask"
              value={android.customScheme}
              onChange={(e) =>
                update({
                  platforms: {
                    ...manifest.platforms,
                    android: { ...android, customScheme: e.currentTarget.value },
                  },
                })
              }
            />
          </SimpleGrid>
          <StringListEditor
            label="App Link Paths"
            placeholder="/app/tasks/*"
            values={android.appLinkPaths}
            onChange={(v) =>
              update({ platforms: { ...manifest.platforms, android: { ...android, appLinkPaths: v } } })
            }
          />
        </Stack>
      </Card>

      {/* Entitlement */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconShieldCheck size={18} />
          <Title order={4}>Entitlement</Title>
        </Group>
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            An empty predicate means every authenticated user sees the app. When several lists are
            set they are combined with AND.
          </Text>
          <StringListEditor
            label="Any of roles"
            description="Caller must hold at least one of these roles."
            placeholder="Staff"
            values={manifest.entitlement.anyOfRoles ?? []}
            onChange={(v) => update({ entitlement: { ...manifest.entitlement, anyOfRoles: v } })}
          />
          <StringListEditor
            label="Any of rights"
            description="Caller must hold at least one of these rights."
            placeholder="tasks.view"
            values={manifest.entitlement.anyOfRights ?? []}
            onChange={(v) => update({ entitlement: { ...manifest.entitlement, anyOfRights: v } })}
          />
          <StringListEditor
            label="All of rights"
            description="Caller must hold every one of these rights."
            placeholder="tasks.admin"
            values={manifest.entitlement.allOfRights ?? []}
            onChange={(v) => update({ entitlement: { ...manifest.entitlement, allOfRights: v } })}
          />
          <Divider label="Entitlement preview" labelPosition="left" />
          <EntitlementPreview entitlement={manifest.entitlement} />
        </Stack>
      </Card>

      {/* Feature flags & metadata */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="xs" mb="md">
          <IconFlag size={18} />
          <Title order={4}>Feature Flags & Metadata</Title>
        </Group>
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Feature Flags
            </Text>
            <Group gap="xs">
              <TextInput
                placeholder="task.ai-prioritizer"
                value={flagKey}
                onChange={(e) => setFlagKey(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="light"
                aria-label="Add feature flag"
                onClick={() => {
                  const k = flagKey.trim();
                  if (k && !(k in featureFlags)) {
                    update({ featureFlags: { ...featureFlags, [k]: true } });
                  }
                  setFlagKey('');
                }}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            {Object.keys(featureFlags).length === 0 ? (
              <Text size="xs" c="dimmed">
                No feature flags.
              </Text>
            ) : (
              <Stack gap={4} mt={4}>
                {Object.entries(featureFlags).map(([key, enabled]) => (
                  <Group key={key} justify="space-between">
                    <Text size="sm">{key}</Text>
                    <Group gap="xs">
                      <Switch
                        checked={enabled}
                        onChange={(e) =>
                          update({
                            featureFlags: { ...featureFlags, [key]: e.currentTarget.checked },
                          })
                        }
                      />
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        aria-label={`Remove flag ${key}`}
                        onClick={() => {
                          const next = { ...featureFlags };
                          delete next[key];
                          update({ featureFlags: next });
                        }}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
          <Divider />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Owner Team"
              placeholder="Tasks"
              value={manifest.metadata?.ownerTeam ?? ''}
              onChange={(e) =>
                update({ metadata: { ...manifest.metadata, ownerTeam: e.currentTarget.value } })
              }
            />
            <TextInput
              label="Support Email"
              placeholder="tasks-support@actschurch.co.za"
              value={manifest.metadata?.supportEmail ?? ''}
              onChange={(e) =>
                update({ metadata: { ...manifest.metadata, supportEmail: e.currentTarget.value } })
              }
            />
            <TextInput
              label="Privacy Policy URL"
              placeholder="https://www.actschurch.co.za/privacy"
              value={manifest.metadata?.privacyPolicy ?? ''}
              onChange={(e) =>
                update({ metadata: { ...manifest.metadata, privacyPolicy: e.currentTarget.value } })
              }
            />
          </SimpleGrid>
          <Switch
            label="Enabled"
            description="Disabled apps are hidden from launchers."
            checked={manifest.enabled}
            onChange={(e) => update({ enabled: e.currentTarget.checked })}
          />
        </Stack>
      </Card>

      {/* Actions */}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={saving}>
          {isEdit ? 'Save Changes' : 'Create App'}
        </Button>
      </Group>

      {/* minAppVersion bump disruption-warning dialog (Appendix E) */}
      <Modal
        opened={bumpDialogOpen}
        onClose={() => setBumpDialogOpen(false)}
        title="Confirm minimum version bump"
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="This is user-disruptive">
            <Text size="sm">
              You are raising <strong>{manifest.displayName || manifest.appId}</strong>'s minimum app
              version from <strong>{originalMinAppVersion}</strong> to{' '}
              <strong>{manifest.minAppVersion}</strong>.
            </Text>
            <Text size="sm" mt="xs">
              Every install older than {manifest.minAppVersion} will be blocked with an update prompt
              on its next cold-start. Users cannot use the app until they update. Make sure the new
              build is live in both stores first.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setBumpDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => {
                setBumpDialogOpen(false);
                finalise();
              }}
            >
              Bump version & save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default AppManifestForm;
